import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import keycloak from './utils/keycloak';
import { AuthService } from './services/AuthService';
import axios from 'axios';
import CustomLoader from './components/loader';
import ErrorPage from './ErrorPages/ErrorPage';
import { IdleTimerProvider, PresenceType } from 'react-idle-timer';
// import HomePage from './pages/Home/Home';
import LandingPage from './pages/Landing/LandingPage';

const container = document.getElementById('root') as HTMLElement;

const Main = () => {
  const keyCloak = keycloak;
  const [count, setCount] = useState<number | undefined>(undefined);
  const [err, setErr] = useState<string | undefined>(undefined);
  const state = useRef<string>('Active');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    getCount();
  }, []);

  useEffect(() => {
    const initializeAuthentication = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
      }
    };
    initializeAuthentication();
  }, []);

  const eventLogger = async (event: string, error?: unknown) => {
    if (event === 'onAuthSuccess') {
      setIsAuthenticated(true);
    }
  };

  const tokenLogger = async (tokens: any) => {
    try {
      if(tokens.token){
        const userProfile = await keycloak.loadUserProfile();
        const role = keycloak.resourceAccess?.[`${process.env.REACT_APP_BE}`]?.roles?.[0];
        if (!role) {
          keycloak.logout();
          return;
        }
        const userData = {
          id: userProfile.id,
          username: userProfile.email,
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          role: role,
        };
        const { setTokenAndPayload } = AuthService();
        setTokenAndPayload(tokens as string, userData);
      }
      return
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const getCount = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/route/users/getUserCount`);
      setCount(response.data.data);
    } catch (error) {
      console.error("Error fetching user count:", error);
      setErr("error");
    }
  };

  const onPresenceChange = (presence: PresenceType) => {
    // Handle presence change here if needed
  };

  const onActive = () => {
    state.current = 'Active';
  };

  const handleOnIdle = () => {
    state.current = 'Idle';
  };
  const initOptions = {
    onLoad: undefined as any, // Use 'login-required' if login should be mandatory
    // onLoad:"login-required",
    checkLoginIframe: false, // Important for Safari
    // pkceMethod: "S256", // Helps with Safari authentication issues
  };
  const onPrompt = () => {
    state.current = 'Prompted';
  };

  // Error handling
  if (err === "error") {
    return <ErrorPage />;
  }

  const renderContent = () => {
    if (count === undefined) {
      return (
        <CustomLoader
          size={200}
          colors="green"
          styling="fixed top-0 left-0 w-screen h-screen bg-gray-800 bg-opacity-50 z-[2]"
        />
      );
    }

    // If error, show the error page
    if (err === "error") {
      return <ErrorPage />;
    }

    return (
      <ReactKeycloakProvider authClient={keyCloak} onEvent={eventLogger} onTokens={tokenLogger} initOptions={initOptions}
      >
        {isAuthenticated ? (
          count > 0 ? (
            <IdleTimerProvider
              timeout={1000 * 60 * 15} // 15 minutes
              onIdle={handleOnIdle}
              promptBeforeIdle={10000}
              throttle={500}
              onPresenceChange={onPresenceChange}
              onPrompt={onPrompt}
              onActive={onActive}
            >
              <App />
            </IdleTimerProvider>
          ) : (
            <LandingPage /> // If count is 0, show LandingPage even if authenticated
          )
        ) : (
          <LandingPage /> // Show LandingPage if user is not authenticated
        )}
      </ReactKeycloakProvider>
    );
  };

  return <div>{renderContent()}</div>;
};

// Render the main component
ReactDOM.createRoot(container).render(<Main />);